import AwsS3Multipart from "@uppy/aws-s3-multipart";
import Uppy, { FileProgress, State, UploadResult, UppyFile } from "@uppy/core";
import Webcam from "@uppy/webcam";
import React from "react";
import { AuthContext } from "./AuthProvider";

import { useToast } from "@chakra-ui/react";
import Audio from "@uppy/audio";
import ImageEditor from "@uppy/image-editor";
import ScreenCapture from "@uppy/screen-capture";
import { FormikContextType, useFormik } from "formik";
import Logger from "../singletons/Logger";
import store, { useAppDispatch } from "../store";
import api, { getErrorMessage } from "../store/api";
import { useCreateTransferMutation } from "../store/api/transfer";
import uppyApi from "../store/api/uppy";

// CSS
import "@uppy/audio/dist/style.min.css";
import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import "@uppy/image-editor/dist/style.min.css";
import "@uppy/screen-capture/dist/style.min.css";
import "@uppy/webcam/dist/style.min.css";
import { useSiteMetadata } from "../components/common/Seo";
import BrowserNotification from "../singletons/BrowserNotification";
import { UserContext } from "./UserProvider";
import "./css/Uppy.css";
import { useCookies } from "react-cookie";
import { useTurnstileContext } from "./TurnstileProvider";

export const uppy = new Uppy({
  debug: process.env.NODE_ENV !== "production",
  autoProceed: false,
  allowMultipleUploadBatches: false,
  restrictions: {
    maxTotalFileSize: 2147483648,
    maxNumberOfFiles: 5,
    minNumberOfFiles: 1,
  },
});

// Server side cannot generate most of this code.
// It's also not necessary, as uppy specifics are client specific.
if (typeof window !== "undefined") {
  uppy
    .use(Webcam)
    .use(AwsS3Multipart, {
      async completeMultipartUpload(file, opts) {
        try {
          const response = await store.dispatch(
            uppyApi.endpoints.completeMultipartUpload.initiate(
              {
                uploadId: opts.uploadId,
                body: { Key: opts.key, Parts: opts.parts },
              },
              { track: false },
            ),
          );

          Logger.debug("Complete Response", { response });

          if ("error" in response) {
            throw new Error(getErrorMessage(response.error));
          }

          return response.data;
        } catch (e) {
          Logger.debug(e);

          throw e;
        }
      },
      async signPart(file, opts) {
        try {
          const response = await store.dispatch(
            uppyApi.endpoints.signPart.initiate({
              uploadId: opts.uploadId,
              partNumber: opts.partNumber,
              params: { key: opts.key },
            }),
          );

          Logger.debug("Sign Response", { response });

          if (response.isError) {
            throw new Error(getErrorMessage(response.error));
          }

          if (!response.data) {
            throw new Error("No data was returned for signPart");
          }

          return response.data;
        } catch (e) {
          Logger.debug(e);

          throw e;
        }
      },
      async listParts(file, opts) {
        try {
          const response = await store.dispatch(
            uppyApi.endpoints.listParts.initiate({
              uploadId: opts.uploadId,
              params: { key: opts.key },
            }),
          );

          Logger.debug("List Response", { response });

          if (response.isError) {
            throw new Error(getErrorMessage(response.error));
          }

          if (!response.data) {
            throw new Error("No data was returned for listParts");
          }

          return response.data;
        } catch (e) {
          Logger.debug(e);

          throw e;
        }
      },
      async abortMultipartUpload(file, opts) {
        try {
          const response = await store.dispatch(
            uppyApi.endpoints.abortMultipartUpload.initiate(
              { uploadId: opts.uploadId, body: { Key: opts.key } },
              { track: false },
            ),
          );

          Logger.debug("Abort Response", { response });

          if ("error" in response) {
            throw new Error(getErrorMessage(response.error));
          }

          return response.data;
        } catch (e) {
          Logger.debug(e);

          throw e;
        }
      },
      async createMultipartUpload(file) {
        try {
          const response = await store.dispatch(
            uppyApi.endpoints.createMultipartUpload.initiate(
              {
                body: {
                  filename: file.name,
                  type: file.type,
                  size: file.size,
                  meta: file.meta,
                },
              },
              { track: false },
            ),
          );

          Logger.debug("Create Response", { response });

          if ("error" in response) {
            throw new Error(getErrorMessage(response.error));
          }

          return response.data;
        } catch (e) {
          Logger.debug(e);

          throw e;
        }
      },
    })
    .use(ImageEditor, {})
    .use(ScreenCapture, {})
    .use(Audio, {});

  uppy.on("upload-success", (file, resp) => {
    if (!file) {
      return;
    }

    uppy.setFileMeta(file.id, {
      ...file.meta,
      uploadedAt: Date.now(),
    });
  });

  uppy.on("error", (error) => {
    // TODO: Toast
  });

  uppy.on("complete", async ({ successful, failed }) => {
    if (failed.length >= 0) {
      // TODO: Toast
    }
  });
}

export interface IUppyFormik {
  title: string;
  from: string;
  recipients: string[];
  toEntry: string;
  linkOnly: boolean;
  message: string;
  password: string;
  expiration?: string;
}

export interface IUppy {
  instance: Uppy;
  state: State<Record<string, unknown>, Record<string, unknown>>;
  formik: FormikContextType<IUppyFormik>;
  startTransfer: () => Promise<void>;
}

export const UppyContext = React.createContext({
  instance: uppy,
  state: uppy.getState(),
} as IUppy);

export const UppyProvider = ({ ...props }) => {
  const { isLoggedIn, userData } = React.useContext(AuthContext);
  const user = React.useContext(UserContext);
  const [cookies, setCookie] = useCookies(['UPLOADS']);

  const [createTransfer] = useCreateTransferMutation();

  const metadata = useSiteMetadata();
  const { consume } = useTurnstileContext();

  const appDispatch = useAppDispatch();

  const [state, setState] = React.useState<
    State<Record<string, unknown>, Record<string, unknown>>
  >(uppy.getState());

  const toast = useToast();

  const formik = useFormik({
    initialValues: {
      title: "",
      from: isLoggedIn()
        ? userData?.UserAttributes.find((attr) => attr.Name === "email")?.Value
        : "",
      recipients: [],
      toEntry: "",
      linkOnly: false,
      message: "",
      password: "",
      expiration: undefined,
    } as IUppyFormik,
    async onSubmit(values, actions) {
      try {
        Logger.debug("Submitting Uppy Formik Form", formik.status);

        if (!values.linkOnly && values.recipients.length <= 0) {
          if (!values.toEntry) {
            throw new Error("Please provide at least one recipient", {
              cause: "toEntry",
            });
          } else {
            values.recipients = [values.toEntry];
          }
        }

        const token = consume();

        let recaptcha = undefined

        if (token) {
          recaptcha = {
            token,
            action: "feedback",
            type: "turnstile",
          };
        }

        const response = await createTransfer({
          body: {
            ...formik.values,
            recipients: values.recipients,
            files: uppy.getFiles().map((file) => {
              return {
                name: file.name,
                size: file.size,
                type: file.type,
              };
            }),
          },
          params: {
            recaptcha,
          },
        });

        if ("error" in response) {
          throw new Error(getErrorMessage(response.error));
        }

        for (const key in uppy.getFiles()) {
          const file = uppy.getFiles()[key];

          uppy.setFileMeta(file.id, {
            ...uppy.getFile(file.id).meta,
            startedAt: Date.now(),
            transferId: response.data.id,
          });
        }

        formik.setStatus("success");
      } catch (e: any) {
        Logger.error(e, formik.status);

        if (e instanceof Error) {
          if (typeof e.cause === "string") {
            actions.setFieldError(e.cause, e.message);
          } else {
            toast({
              title: "An error occurred",
              description: e.message,
              status: "error",
            });
          }
        }

        formik.setStatus("error");

        // TODO: Toast
      } finally {
        Logger.debug("Finished submitting", formik.status);

        actions.setSubmitting(false);
      }
    },
  });

  const startTransfer = async () => {
    Logger.debug("Starting transfer");

    uppy.upload();
  };

  React.useEffect(() => {
    const handler = () => {
      Logger.debug("Updating Uppy State", uppy.getState());

      setState(uppy.getState());
    };

    uppy.on("upload", handler);
    uppy.on("upload-success", handler);
    uppy.on("upload-error", handler);
    uppy.on("cancel-all", handler);
    uppy.on("complete", handler);
    uppy.on("preprocess-complete", handler);
    uppy.on("dashboard:file-edit-complete", handler);
    uppy.on("dashboard:file-edit-start", handler);
    uppy.on("dashboard:modal-closed", handler);
    uppy.on("dashboard:modal-open", handler);
    uppy.on("dashboard:show-panel", handler);
    uppy.on("error", handler);
    uppy.on("file-added", handler);
    uppy.on("file-editor:cancel", handler);
    uppy.on("file-editor:complete", handler);
    uppy.on("file-editor:start", handler);
    uppy.on("file-removed", handler);
    uppy.on("files-added", handler);
    uppy.on("info-hidden", handler);
    uppy.on("info-visible", handler);
    uppy.on("progress", handler);
    uppy.on("reset-progress", handler);
    uppy.on("restriction-failed", handler);
    uppy.on("retry-all", handler);
    uppy.on("thumbnail:generated", handler);
    uppy.on("upload-progress", handler);
    uppy.on("upload-retry", handler);

    return () => {
      uppy.off("upload", handler);
      uppy.off("upload-success", handler);
      uppy.off("upload-error", handler);
      uppy.off("cancel-all", handler);
      uppy.off("complete", handler);
      uppy.off("preprocess-complete", handler);
      uppy.off("dashboard:file-edit-complete", handler);
      uppy.off("dashboard:file-edit-start", handler);
      uppy.off("dashboard:modal-closed", handler);
      uppy.off("dashboard:modal-open", handler);
      uppy.off("dashboard:show-panel", handler);
      uppy.off("error", handler);
      uppy.off("file-added", handler);
      uppy.off("file-editor:cancel", handler);
      uppy.off("file-editor:complete", handler);
      uppy.off("file-editor:start", handler);
      uppy.off("file-removed", handler);
      uppy.off("files-added", handler);
      uppy.off("info-hidden", handler);
      uppy.off("info-visible", handler);
      uppy.off("progress", handler);
      uppy.off("reset-progress", handler);
      uppy.off("restriction-failed", handler);
      uppy.off("retry-all", handler);
      uppy.off("thumbnail:generated", handler);
      uppy.off("upload-progress", handler);
      uppy.off("upload-retry", handler);
    };
  }, [uppy]);

  React.useEffect(() => {
    const handler = () => {
      Logger.debug("Invalidating Transfer Tags");

      appDispatch(api.util.invalidateTags(["Transfer"]));
    };

    uppy.on("error", handler);
    uppy.on("cancel-all", handler);
    uppy.on("upload", handler);

    return () => {
      uppy.off("error", handler);
      uppy.off("cancel-all", handler);
      uppy.off("upload", handler);
    };
  }, []);

  React.useEffect(() => {
    const handler = (result: UploadResult<Record<string, unknown>, Record<string, unknown>>) => {
      // Update cookie value to reflect the number of uploads used by this browser
      const currentUploads = cookies.UPLOADS;
      const expires = new Date(Date.now() + 10 * 24 * 60 * 60 * 1000);

      if (!result.successful.some((item) => item.progress?.percentage !== 100)) {
        if (currentUploads) {
          setCookie("UPLOADS", (parseInt(currentUploads) + 1).toString(), { expires });
        } else {
          setCookie("UPLOADS", "1", { expires });
        }
      }
    };

    uppy.on("complete", handler);

    return () => {
      uppy.off("complete", handler);
    };
  }, [cookies.UPLOADS, setCookie]);

  React.useEffect(() => {
    const handler = (
      file: UppyFile<Record<string, unknown>, Record<string, unknown>>,
    ) => {
      Logger.debug("file-added or file-removed handler called");

      if (uppy.getFiles().length <= 0) {
        Logger.debug("Setting title to an empty string");
        formik.setFieldValue("title", "");

        Logger.debug("Resetting the formik form");
        formik.resetForm();

        Logger.debug("Setting formik status to undefined");
        formik.setStatus(undefined);

        document.title = `${metadata.title} | ${metadata.description}`;
      }

      if (!formik.values.title) {
        Logger.debug(
          "Setting title of transfer to first file in list",
          uppy.getFiles().length > 0 ? uppy.getFiles()[0].name : "",
        );
        formik.setFieldValue(
          "title",
          uppy.getFiles().length > 0 ? uppy.getFiles()[0].name : "",
        );
      }
    };

    uppy.on("file-added", handler);
    uppy.on("file-removed", handler);

    return () => {
      uppy.off("file-added", handler);
      uppy.off("file-removed", handler);
    };
  }, []);

  React.useEffect(() => {
    const handler = () => {
      Logger.debug("Complete handler called");

      if (document.visibilityState === "hidden") {
        if (uppy.getState().error) {
          document.title = `${metadata.title} | Transfer Failed`;

          if (
            user?.notificationPreferences?.browser?.onTransferComplete &&
            BrowserNotification.supported
          ) {
            BrowserNotification.notify({
              title: "Your transfer has failed to upload",
              options: {
                body: uppy.getState().error,
                closeOnVisible: true,
                showAppOnClick: true,
              },
            });
          }
        } else {
          document.title = `${metadata.title} | Transfer Complete`;

          if (
            user?.notificationPreferences?.browser?.onTransferComplete &&
            BrowserNotification.supported
          ) {
            BrowserNotification.notify({
              title: "Your transfer is complete",
              options: { closeOnVisible: true, showAppOnClick: true },
            });
          }
        }
      }
    };

    uppy.on("complete", handler);

    return () => {
      uppy.off("complete", handler);
    };
  }, []);

  React.useEffect(() => {
    const handler = (
      file:
        | UppyFile<Record<string, unknown>, Record<string, unknown>>
        | undefined,
      progress: FileProgress,
    ) => {
      if (typeof window !== "undefined") {
        document.title = `${metadata.title} | Sending Transfer (${Math.floor((progress.bytesUploaded / progress.bytesTotal) * 100)}%)`;
      }
    };

    uppy.on("upload-progress", handler);

    return () => {
      uppy.off("upload-progress", handler);
    };
  }, []);

  // Update restrictions when the user changes
  React.useEffect(() => {
    if (user?.uploadRestrictions) {
      uppy.setOptions({
        restrictions: {
          maxTotalFileSize: user?.uploadRestrictions.maxTransferSize,
          maxNumberOfFiles: user?.uploadRestrictions.maxNumberOfFiles,
          maxFileSize: user?.uploadRestrictions.maxFileSize,
          minNumberOfFiles: 1,
        },
      });
    }
  }, [user?.uploadRestrictions]);

  // As a provider for consistency
  return (
    <UppyContext.Provider
      value={{
        instance: uppy,
        formik,
        startTransfer,
        state,
      }}
    >
      {props.children}
    </UppyContext.Provider>
  );
};
